<template>
  <component :is="embedded ? 'div' : 'v-main'">
    <v-overlay :value="isLoading" z-index="999" :absolute="embedded">
      <v-progress-circular indeterminate size="64" color="primary"></v-progress-circular>
    </v-overlay>
    <component :is="embedded ? 'div' : 'v-container'" v-bind="embedded ? {} : { fluid: true }">
      <component :is="embedded ? 'div' : 'v-card'" v-bind="embedded ? {} : { elevation: 6, rounded: 'xxl' }" :class="embedded ? '' : 'app-card'">
        <v-card-title v-if="!embedded">
          <v-btn class="mx-2" fab x-small color="primary" @click="$router.back()">
            <v-icon dark>mdi-arrow-left</v-icon> </v-btn
          >Assign Stock
        </v-card-title>
        <component :is="embedded ? 'div' : 'v-card-text'">
          <v-alert v-if="submitError" type="error" dense dismissible @input="submitError = ''" class="mb-4">
            {{ submitError }}
          </v-alert>
          <v-row>
            <v-col sm="6">
              <v-select
                v-model="form.assigned_to_id"
                :items="filteredUsers"
                item-text="display_name"
                item-value="id"
                outlined
                prepend-inner-icon="mdi-account"
                :error-messages="errors.assigned_to_id"
                :no-data-text="'No users available'"
              >
                <template v-slot:label>Assign To <span style="color:#f44336;font-weight:bold;">*</span></template>
              </v-select>
            </v-col>

            <v-col sm="6">
              <v-select
                v-model="form.transporter_manager_id"
                :items="transporterManagers"
                item-text="display_name"
                item-value="id"
                label="Transporter Manager"
                outlined
                clearable
                prepend-inner-icon="mdi-truck-delivery"
                :error-messages="errors.transporter_manager_id"
                :no-data-text="'No transporter managers available'"
                hint="Select the transporter who will handle delivery"
                persistent-hint
              ></v-select>
            </v-col>

            <v-col sm="4">
              <v-switch
                class="mt-0 ml-5"
                v-model="form.is_active"
                inset
                :label="`Is Active: ${form.is_active ? 'YES' : 'NO'}`"
              ></v-switch>
            </v-col>
          </v-row>

          <!-- Address Section -->
          <v-row v-if="form.assigned_to_id">
            <v-col cols="12">
              <v-subheader class="text-h6 px-0">
                <v-icon left color="primary">mdi-map-marker</v-icon>
                Delivery Address
              </v-subheader>
              <v-divider></v-divider>
            </v-col>

            <v-col sm="12">
              <v-textarea
                v-model="form.address"
                label="Address"
                outlined
                rows="3"
                prepend-inner-icon="mdi-home-map-marker"
                :error-messages="errors.address"
                hint="Enter the delivery address for this user"
                persistent-hint
              ></v-textarea>
            </v-col>

            <v-col sm="4">
              <v-select
                v-model="form.country_id"
                :items="countries"
                item-text="description"
                item-value="id"
                label="Country"
                outlined
                clearable
                prepend-inner-icon="mdi-earth"
                :error-messages="errors.country_id"
                @change="onCountryChange"
              ></v-select>
            </v-col>

            <v-col sm="4">
              <v-select
                v-model="form.state_id"
                :items="states"
                item-text="description"
                item-value="id"
                label="State"
                outlined
                clearable
                prepend-inner-icon="mdi-map"
                :error-messages="errors.state_id"
                @change="onStateChange"
                :disabled="!form.country_id"
              ></v-select>
            </v-col>

            <v-col sm="4">
              <v-select
                v-model="form.city_id"
                :items="cities"
                item-text="description"
                item-value="id"
                label="City"
                outlined
                clearable
                prepend-inner-icon="mdi-city"
                :error-messages="errors.city_id"
                :disabled="!form.state_id"
              ></v-select>
            </v-col>
          </v-row>

          <AppSectionHeader label="Add Products" />
          <v-row align="center">
            <v-col sm="3">
              <v-autocomplete
                v-model="picker.product_id"
                :items="filteredProducts"
                item-text="name"
                item-value="id"
                label="Product"
                outlined
                dense
                hide-details
                @change="onProductPick"
                :no-data-text="
                  products.length === 0
                    ? 'Loading products...'
                    : 'No products available'
                "
              ></v-autocomplete>
            </v-col>
            <v-col sm="3">
              <v-autocomplete
                v-model="picker.variant_id"
                :items="pickerVariants"
                :item-text="formatVariantName"
                item-value="id"
                label="Variant"
                outlined
                dense
                hide-details
                :disabled="!picker.product_id"
                @change="onVariantPick"
                :no-data-text="
                  !picker.product_id
                    ? 'Select a product first'
                    : 'No variants available for this product'
                "
              ></v-autocomplete>
            </v-col>
            <v-col sm="2">
              <v-text-field
                v-model.number="picker.quantity"
                type="number"
                min="1"
                label="Quantity"
                outlined
                dense
                hide-details
              ></v-text-field>
            </v-col>
            <v-col sm="2">
              <v-text-field
                v-model.number="picker.unit_price"
                type="number"
                min="0"
                label="Unit Price"
                outlined
                dense
                hide-details
              ></v-text-field>
            </v-col>
            <v-col sm="2">
              <v-btn block color="primary" @click="addToItems">
                <v-icon left small>mdi-plus</v-icon> Add
              </v-btn>
            </v-col>
          </v-row>
          <div class="text-caption grey--text mt-2" v-if="pickerCompanyStock !== null">
            Company stock available for this variant: {{ pickerCompanyStock }}
          </div>
          <div class="text-caption error--text mt-1" v-if="pickerError">
            {{ pickerError }}
          </div>
          <div class="text-caption error--text mt-1" v-if="errors.items">
            {{ Array.isArray(errors.items) ? errors.items[0] : errors.items }}
          </div>

          <!-- Assignment Items -->
          <v-card outlined class="mt-6" v-if="form.items.length">
            <v-card-title class="text-subtitle-1 font-weight-bold py-3">
              Items to Assign ({{ form.items.length }})
            </v-card-title>
            <v-divider></v-divider>
            <div v-for="(item, index) in form.items" :key="item._key">
              <v-row align="center" class="px-4 py-2" no-gutters>
                <v-col cols="auto">
                  <v-avatar color="deep-purple lighten-4" size="32">
                    <span class="deep-purple--text font-weight-bold">{{ index + 1 }}</span>
                  </v-avatar>
                </v-col>
                <v-col class="ml-4">
                  <div class="font-weight-medium">
                    {{ item.product_name }}
                    <span v-if="item.variant_name" class="font-weight-regular grey--text"> - {{ item.variant_name }}</span>
                  </div>
                  <div class="text-caption grey--text">Qty: {{ item.quantity }}</div>
                </v-col>
                <v-col cols="auto" class="mx-4 grey--text mono-figure">
                  @ ₹{{ Number(item.unit_price).toFixed(2) }}
                </v-col>
                <v-col cols="auto" class="mx-4 font-weight-bold primary--text mono-figure">
                  ₹{{ lineTotal(item) }}
                </v-col>
                <v-col cols="auto">
                  <v-btn icon color="error" @click="removeItem(index)">
                    <v-icon>mdi-delete</v-icon>
                  </v-btn>
                </v-col>
              </v-row>
              <v-divider v-if="index < form.items.length - 1"></v-divider>
            </div>
            <v-divider></v-divider>
            <div class="px-4 py-3">
              <div class="d-flex justify-space-between font-weight-bold">
                <span>Total</span>
                <span class="primary--text mono-figure">₹{{ totalAmount }}</span>
              </div>
            </div>
          </v-card>

          <AppSectionHeader label="Payment" />
          <v-row class="mt-2">
            <v-col sm="3">
              <v-text-field
                :value="totalAmount"
                label="Total (Loan Amount)"
                outlined
                dense
                readonly
                class="mono-figure"
              ></v-text-field>
            </v-col>
            <v-col sm="3">
              <v-menu
                v-model="dueDateMenu"
                :close-on-content-click="false"
                transition="scale-transition"
                offset-y
                min-width="auto"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    v-model="form.due_date"
                    outlined
                    dense
                    readonly
                    label="Pay By (optional)"
                    v-bind="attrs"
                    v-on="on"
                  ></v-text-field>
                </template>
                <v-date-picker
                  v-model="form.due_date"
                  @input="dueDateMenu = false"
                ></v-date-picker>
              </v-menu>
            </v-col>
            <v-col sm="6" class="d-flex align-center">
              <span class="text-caption grey--text">
                Nothing paid now - this stock is assigned on credit, and
                {{ assigneeLabel }} will repay this amount later, in full or a
                little at a time, from the Stock Payments screen.
              </span>
            </v-col>
            <v-col sm="12">
              <v-textarea
                v-model="form.remarks"
                outlined
                label="Remarks"
                prepend-inner-icon="mdi-note-text"
                rows="2"
                auto-grow
                :error-messages="errors.remarks"
              ></v-textarea>
            </v-col>
          </v-row>

          <v-divider class="mt-5 mb-5"> </v-divider>
          <v-btn
            :loading="isLoading"
            :disabled="isLoading || !form.items.length"
            rounded
            color="primary"
            dark
            @click="save"
          >
            Save Assigned Stock
          </v-btn>
        </component>
      </component>

      <!-- Success Dialog -->
      <v-dialog v-model="is_created" width="600">
        <v-card rounded="xxl">
          <v-container>
            <v-row align="center">
              <v-col sm="3" align="center">
                <v-icon
                  large
                  style="color: green; font-size: 90px; width: 200px !important"
                >
                  mdi-check-circle
                </v-icon>
              </v-col>
              <v-col sm="9" align="center">
                <v-card-text>
                  <v-row>
                    <v-col>
                      <h1 style="color: green">Success!</h1>
                      <br />
                      <h3>Stock assigned successfully. An invoice has been generated.</h3>
                      <br />
                      <v-btn color="primary" outlined class="mr-2" v-if="createdAssignmentId" @click="goToInvoice">
                        View Invoice
                      </v-btn>
                      <v-btn color="success" @click="goToList"> OK </v-btn>
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-col>
            </v-row>
          </v-container>
        </v-card>
      </v-dialog>
    </component>
  </component>
</template>

<script>
import axios from "axios";
import AppSectionHeader from "@/components/AppSectionHeader.vue";

export default {
  name: "AssignedStockCreatePage",
  components: { AppSectionHeader },
  props: {
    // When true, this renders inside a dialog (opened from the Assigned
    // Stocks list "+" button) instead of its own routed page.
    embedded: { type: Boolean, default: false },
  },
  data() {
    return {
      picker: {
        product_id: null,
        variant_id: null,
        quantity: 1,
        unit_price: null,
      },
      pickerError: "",
      submitError: "",
      form: {
        items: [],
        assigned_to_id: null,
        transporter_manager_id: null,
        address: "",
        city_id: null,
        state_id: null,
        country_id: null,
        due_date: null,
        remarks: "",
        is_active: true,
      },
      dueDateMenu: false,
      products: [],
      variants: [],
      users: [],
      allUsers: [],
      transporterManagers: [],
      countries: [],
      states: [],
      allStates: [],
      cities: [],
      allCities: [],
      companyStocks: [],
      companyStockMap: {},
      isLoading: false,
      is_created: false,
      createdAssignmentId: null,
    };
  },
  computed: {
    errors() {
      return this.$store.getters["errors/getErrors"] || {};
    },
    filteredProducts() {
      if (!this.products || this.products.length === 0) return [];
      return this.products
        .filter((product) => {
          return (
            product.product_variants && product.product_variants.length > 0
          );
        })
        .map((product) => {
          const variantCount = product.product_variants.length;
          return {
            ...product,
            name: `${product.name} (${variantCount} variant${
              variantCount > 1 ? "s" : ""
            })`,
          };
        });
    },
    pickerVariants() {
      return this.variants;
    },
    filteredUsers() {
      return this.allUsers;
    },
    pickerCompanyStock() {
      if (!this.picker.variant_id) return null;
      const stock = this.companyStockMap[this.picker.variant_id];
      return stock ? parseInt(stock.quantity) || 0 : 0;
    },
    totalAmount() {
      return this.form.items
        .reduce((sum, item) => sum + Number(this.lineTotal(item)), 0)
        .toFixed(2);
    },
    assigneeLabel() {
      const user = this.allUsers.find((u) => u.id === this.form.assigned_to_id);
      return user ? user.first_name : "the assignee";
    },
  },
  watch: {
    "form.assigned_to_id"(newVal, oldVal) {
      if (newVal && newVal !== oldVal) {
        this.fetchUserLocation(newVal);
      }
    },
  },
  async mounted() {
    this.$store.dispatch("errors/setErrors", {});
    this.isLoading = true;
    await Promise.all([this.fetchMasters(), this.getCompanyStocks()]);
    this.isLoading = false;
  },
  methods: {
    async fetchMasters() {
      try {
        const response = await axios.get("/masters", {
          params: { context: "assign_stock" },
        });

        const data = response.data;

        this.products = (data.products_with_variants || []).map((product) => ({
          id: product.id,
          name: product.name,
          description: product.description,
          product_variants: (product.variants || []).map((v) => ({
            id: v.id,
            product_id: v.product_id,
            color: v.color,
            size: v.size,
            price: v.price,
            discount_percentage: v.discount_percentage,
            discount_amount: v.discount_amount,
            available_quantity: v.available_quantity,
          })),
        }));

        const distributors = (data.distributors || []).map((user) => ({
          id: user.id,
          first_name: user.name.split(" ")[0],
          last_name: user.name.split(" ").slice(1).join(" "),
          phone: user.phone,
          email: user.email,
          position: { name: user.position },
          display_name: `${user.name} (${user.phone || "N/A"}) (${user.position || ""})`,
        }));

        const franchises = (data.franchises || []).map((user) => ({
          id: user.id,
          first_name: user.name.split(" ")[0],
          last_name: user.name.split(" ").slice(1).join(" "),
          phone: user.phone,
          email: user.email,
          position: { name: user.position },
          display_name: `${user.name} (${user.phone || "N/A"}) (${user.position || ""})`,
        }));

        const saleExecutives = (data.sale_executives || []).map((user) => ({
          id: user.id,
          first_name: user.name.split(" ")[0],
          last_name: user.name.split(" ").slice(1).join(" "),
          phone: user.phone,
          email: user.email,
          position: { name: user.position },
          display_name: `${user.name} (${user.phone || "N/A"}) (${user.position || ""})`,
        }));

        this.allUsers = [...distributors, ...franchises, ...saleExecutives];
        this.users = this.allUsers;

        this.transporterManagers = (data.transporter_managers || []).map((user) => ({
          id: user.id,
          first_name: user.name.split(" ")[0],
          last_name: user.name.split(" ").slice(1).join(" "),
          phone: user.phone,
          email: user.email,
          display_name: `${user.name} (${user.phone || "N/A"})`,
        }));

        this.countries = (data.countries || []).map((c) => ({
          id: c.id,
          code: c.code,
          description: c.name,
        }));

        this.allStates = (data.states || []).map((s) => ({
          id: s.id,
          code: s.code,
          description: s.name,
          parent_id: s.country_id,
        }));

        this.allCities = (data.cities || []).map((c) => ({
          id: c.id,
          code: c.code,
          description: c.name,
          parent_id: c.state_id,
        }));
      } catch (error) {
        console.error("Error fetching masters:", error);
      }
    },
    formatVariantName(variant) {
      let name = [];
      if (variant.color) name.push(`Color: ${variant.color}`);
      if (variant.size) name.push(`Size: ${variant.size}`);
      if (variant.price) name.push(`₹${variant.price}`);
      return name.length > 0 ? name.join(" | ") : "Variant";
    },
    async fetchUserLocation(userId) {
      try {
        const response = await axios.get(`/users/${userId}/location`);
        if (response.data.success && response.data.data) {
          const location = response.data.data;
          this.form.address = location.address || "";
          this.form.country_id = location.country_id || null;
          this.form.state_id = location.state_id || null;
          this.form.city_id = location.city_id || null;

          if (location.country_id) {
            this.filterStates(location.country_id);
          }
          if (location.state_id) {
            this.filterCities(location.state_id);
          }
        } else {
          this.form.address = "";
          this.form.country_id = null;
          this.form.state_id = null;
          this.form.city_id = null;
          this.states = [];
          this.cities = [];
        }
      } catch (error) {
        console.error("Error fetching user location:", error);
      }
    },
    filterStates(countryId) {
      this.states = this.allStates.filter((s) => s.parent_id === countryId);
    },
    filterCities(stateId) {
      this.cities = this.allCities.filter((c) => c.parent_id === stateId);
    },
    onCountryChange() {
      this.form.state_id = null;
      this.form.city_id = null;
      this.states = [];
      this.cities = [];
      if (this.form.country_id) {
        this.filterStates(this.form.country_id);
      }
    },
    onStateChange() {
      this.form.city_id = null;
      this.cities = [];
      if (this.form.state_id) {
        this.filterCities(this.form.state_id);
      }
    },
    async getCompanyStocks() {
      try {
        const response = await axios.get("/company_stocks");
        this.companyStocks = response.data.data || [];

        this.companyStockMap = {};
        this.companyStocks.forEach((stock) => {
          if (stock.variant_id) {
            this.companyStockMap[stock.variant_id] = stock;
          }
        });
      } catch (error) {
        console.error("Error fetching company stocks:", error);
      }
    },
    onProductPick() {
      this.picker.variant_id = null;
      this.picker.unit_price = null;
      this.variants = [];

      if (this.picker.product_id) {
        const product = this.products.find((p) => p.id === this.picker.product_id);
        if (product && product.product_variants) {
          this.variants = product.product_variants;
        }
      }
    },
    onVariantPick() {
      if (this.picker.variant_id) {
        const variant = this.variants.find((v) => v.id === this.picker.variant_id);
        this.picker.unit_price = variant ? Number(variant.price) || 0 : null;
      } else {
        this.picker.unit_price = null;
      }
    },
    lineTotal(item) {
      return (Number(item.unit_price) * Number(item.quantity)).toFixed(2);
    },
    addToItems() {
      this.pickerError = "";

      if (!this.picker.product_id || !this.picker.variant_id) {
        this.pickerError = "Please select a product and variant.";
        return;
      }
      if (!this.picker.quantity || this.picker.quantity < 1) {
        this.pickerError = "Quantity must be at least 1.";
        return;
      }
      if (this.form.items.some((i) => i.variant_id === this.picker.variant_id)) {
        this.pickerError = "This variant has already been added to this assignment.";
        return;
      }
      if (this.picker.quantity > this.pickerCompanyStock) {
        this.pickerError = `Only ${this.pickerCompanyStock} units are available in company stock for this variant.`;
        return;
      }

      const product = this.products.find((p) => p.id === this.picker.product_id);
      const variant = this.variants.find((v) => v.id === this.picker.variant_id);

      this.form.items.push({
        _key: `${this.picker.product_id}_${this.picker.variant_id}_${Date.now()}`,
        product_id: this.picker.product_id,
        product_name: product ? product.name.replace(/\s*\(\d+ variants?\)$/, "") : "",
        variant_id: this.picker.variant_id,
        variant_name: variant ? this.formatVariantName(variant) : "",
        quantity: this.picker.quantity,
        unit_price: this.picker.unit_price || 0,
      });

      this.picker = { product_id: null, variant_id: null, quantity: 1, unit_price: null };
      this.variants = [];
    },
    removeItem(index) {
      this.form.items.splice(index, 1);
    },
    async save() {
      try {
        this.isLoading = true;
        this.$store.dispatch("errors/setErrors", {});

        let validationErrors = {};
        if (!this.form.items.length) {
          validationErrors.items = ["Please add at least one product to assign."];
        }
        if (!this.form.assigned_to_id) {
          validationErrors.assigned_to_id = ["Please select a user to assign to"];
        }

        if (Object.keys(validationErrors).length > 0) {
          this.$store.dispatch("errors/setErrors", validationErrors);
          this.isLoading = false;
          return;
        }

        const payload = {
          items: this.form.items.map((item) => ({
            product_id: item.product_id,
            variant_id: item.variant_id,
            quantity: item.quantity,
            unit_price: item.unit_price,
          })),
          assigned_to_id: this.form.assigned_to_id,
          transporter_manager_id: this.form.transporter_manager_id,
          address: this.form.address,
          city_id: this.form.city_id,
          state_id: this.form.state_id,
          country_id: this.form.country_id,
          due_date: this.form.due_date,
          remarks: this.form.remarks,
          is_active: this.form.is_active,
        };

        this.submitError = "";
        const response = await axios.post("/assigned_stocks", payload);
        this.createdAssignmentId = response.data.data ? response.data.data.id : null;
        this.isLoading = false;
        this.is_created = true;
      } catch (e) {
        console.log(e);
        this.isLoading = false;

        const data = e.response && e.response.data;
        // Flatten whatever shape the backend sent (a single message, a
        // validator errors bag keyed like "items.0.quantity", or a bare
        // {error: "..."} from an unhandled exception) into one readable
        // string, so a failed save is never silently invisible.
        let message =
          (data && (data.sub_title || data.message)) ||
          (!e.response ? "Could not reach the server. Please check your connection and try again." : "");

        if (data && data.data && typeof data.data === "object") {
          const fieldMessages = Object.values(data.data)
            .flat()
            .filter(Boolean);
          if (fieldMessages.length) {
            message = message ? `${message} - ${fieldMessages.join(" ")}` : fieldMessages.join(" ");
          }
          this.$store.dispatch("errors/setErrors", data.data);
        }

        this.submitError = message || "Failed to assign stock. Please try again.";
        if (this.$toast) {
          this.$toast.error(this.submitError);
        }
      }
    },
    goToInvoice() {
      if (this.createdAssignmentId) {
        if (this.embedded) {
          this.$emit("saved");
          this.$emit("close");
        }
        this.$router.push(`/stock-assignments/${this.encryptIt(this.createdAssignmentId)}`);
      }
    },
    goToList() {
      if (this.embedded) {
        this.$emit("saved");
        this.$emit("close");
      } else {
        this.$router.push("/assigned_stocks");
      }
    },
  },
};
</script>

<style scoped></style>
